import { Container, Graphics, Text } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import { bestRecord, highestClearedTier, maxUnlockedTier, tierLabel } from '../sim/run/tiers';
import { formatRunTime } from './EndScene';
import { CHARACTERS, isCharacterId } from '../sim/characters/CharacterDefs';

/**
 * Local leaderboard: per-tier best clear times + lifetime stats. Read-only;
 * records are written by applyRunEnd (clean wins only — cheat runs excluded).
 */
export class RecordsScene implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private acceptDelay = 8;

  constructor(private readonly ctx: GameContext) {
    const w = ctx.app.screen.width;
    const h = ctx.app.screen.height;
    const data = ctx.profile.data;

    this.root.addChild(new Graphics().rect(0, 0, w, h).fill(Palette.bg));

    const title = new Text({
      text: 'RECORDS',
      style: {
        fontFamily: 'monospace', fontSize: 34, fontWeight: 'bold',
        fill: Palette.hudText, letterSpacing: 6,
      },
    });
    title.anchor.set(0.5, 0);
    title.position.set(w / 2, 40);
    this.root.addChild(title);

    const hc = highestClearedTier(data);
    const headline = new Text({
      text: hc >= 1
        ? `highest cleared   ${tierLabel(hc)}`
        : hc === 0
          ? 'the descent cleared — the ladder awaits'
          : 'no clears yet — slay the boss to set a record',
      style: { fontFamily: 'monospace', fontSize: 15, fill: Palette.gold },
    });
    headline.anchor.set(0.5, 0);
    headline.position.set(w / 2, 90);
    this.root.addChild(headline);

    // Table: base game + every unlocked tier — best time, who set it, kills.
    const rows: { label: string; detail: string; recorded: boolean }[] = [];
    const maxShown = Math.max(maxUnlockedTier(data), hc);
    for (let t = 0; t <= maxShown; t++) {
      const best = bestRecord(data, t);
      let detail = '— unbeaten —';
      if (best !== undefined) {
        // Legacy (pre-rework) records carry no character/kills — time only.
        const who = isCharacterId(best.character)
          ? `  ·  ${CHARACTERS[best.character].name.split(',')[0]}`
          : '';
        const kills = best.kills > 0 ? `  ·  ${best.kills} kills` : '';
        detail = `best ${formatRunTime(best.seconds)}${who}${kills}`;
      }
      rows.push({
        label: t === 0 ? 'the descent' : tierLabel(t).toLowerCase(),
        detail,
        recorded: best !== undefined,
      });
    }
    const tableY = 140;
    rows.forEach((row, i) => {
      const line = new Text({
        text: `${row.label.padEnd(16)}${row.detail}`,
        style: {
          fontFamily: 'monospace', fontSize: 15,
          fill: row.recorded ? Palette.hudText : Palette.hudDim,
        },
      });
      line.anchor.set(0.5, 0);
      line.position.set(w / 2, tableY + i * 26);
      this.root.addChild(line);
    });

    const s = data.stats;
    const footer = new Text({
      text: `wins ${s.wins}   deaths ${s.deaths}   deepest floor ${s.bestFloor}   ` +
        `descents ${s.runsStarted}\nlifetime kills ${s.totalKills}   lifetime gems ${s.totalGems}`,
      style: {
        fontFamily: 'monospace', fontSize: 13, fill: Palette.hudDim,
        align: 'center', lineHeight: 20,
      },
    });
    footer.anchor.set(0.5, 0);
    footer.position.set(w / 2, tableY + rows.length * 26 + 30);
    this.root.addChild(footer);

    const hint = new Text({
      text: 'back  [Esc / Ⓑ]',
      style: { fontFamily: 'monospace', fontSize: 12, fill: Palette.hudDim },
    });
    hint.anchor.set(0.5, 1);
    hint.position.set(w / 2, h - 18);
    this.root.addChild(hint);
  }

  enter(): void {}
  exit(): void {}

  update(_dt: number, input: InputSnapshot): void {
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }
    if (input.cancelPressed || input.confirmPressed) this.ctx.scenes.pop();
  }

  render(_alpha: number): void {}
}
